/**
 * Module that is responsible for fetching and caching the favorites list.
 * The favorites-list-api is only responsible for fetching the data from the api, this module in contrast
 * adds some caching functionality, so that the favorites lists must not be fetched each time they are used.
 */
import { getAllFavorites } from './api/favorites-api';

export interface FavoritesId {
  id: string;
  favoritesItemId: string;
}

class FavoritesListDataModule {
  /**
   * All IDs of resources in the favorites list.
   */
  productIdsInFavoritesList: FavoritesId[] = null;

  /**
   * Get the favorites lists. If they have not been cached yet, this
   * function makes a request to the favorites list API. After that it will return the cached favorites list IDs.
   * @param forceUpdate Boolean If the favorites lists should be loaded from the API even if there exists a cached version.
   */
  async getCachedFavoritesListResourceIds(forceUpdate = false): Promise<FavoritesId[]> {
    if (this.productIdsInFavoritesList && !forceUpdate) {
      // If the favorites lists have been fetched already, return the cached ones
      return Promise.resolve(this.productIdsInFavoritesList);
    }

    if (document.querySelector('html').classList.contains('user-logged-out')) {
      return null;
    }

    return new Promise((resolve, reject) => {
      // Otherwise call the favorites lists API
      getAllFavorites()
        .then((favorites) => {
          this.productIdsInFavoritesList = favorites.map((favorite) => {
            return { id: favorite.nodeid, favoritesItemId: favorite.favoritesItemId };
          });
          resolve(this.productIdsInFavoritesList);
        })
        .catch(() => {
          reject();
        });
    });
  }

  /**
   * Remove the resource with the given ID from the cached resource favorites.
   */
  removeResourceFromCachedFavoritesList(resourceId): void {
    if (this.productIdsInFavoritesList) {
      const index = this.productIdsInFavoritesList.findIndex((favorite) => favorite.id === resourceId);
      if (index >= 0) {
        this.productIdsInFavoritesList.splice(index, 1);
      }
    }
  }

  /**
   * Adds the resource with the given ID to the cached resource favorites.
   */
  addResourceToCachedFavoritesList(resourceId: string, resourceFavoritesItemId: string): void {
    if (!this.isFavorite(resourceId, this.productIdsInFavoritesList)) {
      this.productIdsInFavoritesList.push({ id: resourceId, favoritesItemId: resourceFavoritesItemId });
    }
  }

  /**
   * Checks if the given resource ID is included in the given array of favorites.
   */
  isFavorite(resourceId, favorites): boolean {
    let isFavorite = false;
    for (const favorite of favorites) {
      if (favorite.id === resourceId) {
        isFavorite = true;
      }
    }

    return isFavorite;
  }
}

const favoritesListDataModule = new FavoritesListDataModule();
export { favoritesListDataModule as favoritesListData };
